import type { ManageAgreementSection } from '../components/manage/ManageAgreementTab/ManageAgreementTab';
import { isCompletableReason } from './cancellationCopy';
import type { Agreement } from '../types/agreements.types';
import type { CancelAction, CancellationProgress, CancellationRecord, CompletableReason } from '../types/cancellation.types';

export const MANAGE_SECTIONS: Record<'cancellations', ManageAgreementSection> = {
    cancellations: {
        id: 'cancellations',
        label: 'Cancellations',
        description: 'Cancel this agreement, or follow a cancellation in progress.',
    },
};

export function manageSections(cancelAction: CancelAction | undefined): ManageAgreementSection[] {
    const showCancellations = cancelAction !== undefined && cancelAction.reasonCode !== 'not_permitted';
    return showCancellations ? [MANAGE_SECTIONS.cancellations] : [];
}

export function completableReasonFor(progress: CancellationProgress | null): CompletableReason | null {
    if (progress?.kind !== 'awaiting_motorplan') return null;
    return isCompletableReason(progress.reason) ? progress.reason : null;
}

export function isSettlementInFlight(agreement: Pick<Agreement, 'status' | 'cancellation'> | undefined): boolean {
    const progress = agreement?.status === 'pending_cancellation' ? agreement.cancellation?.progress : undefined;
    return progress?.kind === 'settling' && progress.refundPending !== true;
}

export function isCancellationAccepted(agreement: Pick<Agreement, 'status' | 'cancellation'> | undefined): boolean {
    if (agreement?.status === 'cancelled') return Boolean(agreement.cancellation);
    const progress = agreement?.status === 'pending_cancellation' ? agreement.cancellation?.progress : undefined;
    return progress?.kind === 'settling' && progress.refundPending === true;
}

export type CancellationSectionView =
    | { kind: 'start' }
    | { kind: 'unavailable'; message: string }
    | { kind: 'in_progress'; progress: CancellationProgress; completableReason: CompletableReason | null }
    | { kind: 'none' };

interface ViewInput {
    status: string;
    cancelAction: CancelAction;
    cancellation: CancellationRecord | null | undefined;
    canComplete: boolean;
}

const isFrozenByClaim = (status: string, cancelAction: CancelAction): boolean =>
    status === 'pending_claim' && cancelAction.reasonCode === 'cancellation_in_progress';

export function cancellationSectionView({ status, cancelAction, cancellation, canComplete }: ViewInput): CancellationSectionView {
    const pending = status === 'pending_cancellation';
    if ((pending || isFrozenByClaim(status, cancelAction)) && cancellation?.progress) {
        const completableReason = canComplete && pending ? completableReasonFor(cancellation.progress) : null;
        return { kind: 'in_progress', progress: cancellation.progress, completableReason };
    }
    if (cancelAction.available) return { kind: 'start' };
    if (cancellation && status === 'cancelled') return { kind: 'none' };
    return cancelAction.message ? { kind: 'unavailable', message: cancelAction.message } : { kind: 'none' };
}
